import React, { useEffect, useMemo, useState } from 'react';
import { getAll, update } from '../api';
import { useFormBuilder } from '../utils/useFormBuilder';
import { getWorkPackages, shareWorkPackage } from '../api/work-packagesAPI';
import {
  createWorkOrderForm,
  deleteWorkOrderForm,
  updateWorkOrderForm,
} from '../api/work-order-formsAPI';
import { getFormById } from '../api/formsAPI';
import { createFormData } from '../api/form-dataAPI';
import {
  createWorkOrderFormAction,
  deleteWorkOrderFormAction,
  getWorkOrderFormActionsByWorkOrderFormId,
} from '../api/work-order-form-actionsAPI';
import { useSetting } from '../utils/AppSettingsContext';
import ContractorWizardModal from './common/ContractorWizardModal';
import { useContractorWizard } from '../hook/useContractorWizard';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';

const ADD_CONTRACTOR_VALUE = '__add_contractor__';
const EXPIRY_OPTIONS = [1, 2, 3, 4, 5, 7, 14, 30].map((days) => ({ value: days, label: `${days} Days (${days})` }));

const toDateTimeValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getDurationHours = (startAt, dueDate) => {
  const start = startAt ? new Date(startAt).getTime() : NaN;
  const end = dueDate ? new Date(dueDate).getTime() : NaN;
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Number(((end - start) / (1000 * 60 * 60)).toFixed(2));
};

const getContractorImageUrl = (contractor) => {
  const keyOrUrl = contractor?.profileimage || contractor?.profileImage;
  if (!keyOrUrl) return `${process.env.REACT_APP_SPACES_BASE}/user.png`;
  if (String(keyOrUrl).startsWith('http')) return keyOrUrl;
  return `${process.env.REACT_APP_SPACES_BASE}/${keyOrUrl}`;
};

const getDefaultStartAtFromShift = (shiftStartTime) => {
  const now = new Date();
  const [hoursRaw = '08', minutesRaw = '00'] = String(shiftStartTime || '08:00:00').split(':');
  const hours = Number.parseInt(hoursRaw, 10);
  const minutes = Number.parseInt(minutesRaw, 10);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), Number.isFinite(hours) ? hours : 8, Number.isFinite(minutes) ? minutes : 0, 0, 0);
  return toDateTimeValue(start);
};

const parseDepartmentIds = (value) => {
  if (value === null || value === undefined || value === '') return [];

  let raw = value;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (_error) {
      raw = raw.split(',');
    }
  }

  const values = Array.isArray(raw)
    ? raw
    : typeof raw === 'object'
      ? Object.values(raw)
      : [raw];

  return values
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
};

const toContractorIdArray = (value) => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item) => (typeof item === 'object' ? Number(item?.id) : Number(item)))
    .filter((item) => Number.isFinite(item));
};

const getAssignmentDepartmentIds = ({ form, workOrder, isFormShare }) => {
  if (!isFormShare) return parseDepartmentIds(workOrder?.departmentId);

  const assignmentCandidates = [
    form?.assignment,
    form?.formAssignment,
    ...(Array.isArray(form?.assignments) ? form.assignments : []),
    ...(Array.isArray(form?.formAssignments) ? form.formAssignments : []),
  ];

  const departmentIds = assignmentCandidates.flatMap((assignment) => parseDepartmentIds(assignment?.departmentIds));
  if (departmentIds.length > 0) return [...new Set(departmentIds)];

  return parseDepartmentIds(form?.departmentId);
};

const buildInitialFormData = ({ workOrder, form, shiftStartTime }) => {
  if (form) {
    return {
      globalContractorId: null,
      reportTo: null,
      startAt: getDefaultStartAtFromShift(shiftStartTime),
      duration: 0,
      jobExpiryDays: 2,
      expiryDate: '',
      meetingPoint: '',
      location: '',
      instructions: '',
    };
  }

  return {
    globalContractorId: null,
    reportTo: '',
    startAt: toDateTimeValue(workOrder?.scheduledAt),
    duration: getDurationHours(workOrder?.scheduledAt, workOrder?.dueDate),
    jobExpiryDays: 2,
    expiryDate: '',
    meetingPoint: workOrder?.meetingPoint || '',
    location: workOrder?.locationAddress || '',
    instructions: '',
  };
};

const WorkOrderShare = ({ workOrder, form, show, onClose, onSaved }) => {
  const userProfile = useUserProfile();
  const shiftStartTime = useSetting('shift_start_time', '08:00:00');
  const isFormShare = Boolean(form);

  const [formData, setFormData] = useState(buildInitialFormData({ workOrder, form, shiftStartTime }));
  const [errors, setErrors] = useState({});
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [existingWorkPackages, setExistingWorkPackages] = useState([]);
  const [selectedWorkPackageId, setSelectedWorkPackageId] = useState('new');

  const loadData = async () => {
    const [contractorResponse, supplierResponse, usersResponse] = await Promise.all([getAll('contractors'), getAll('suppliers'), getAll('users')]);
    setContractors(Array.isArray(contractorResponse?.data) ? contractorResponse.data : Array.isArray(contractorResponse) ? contractorResponse : []);
    setSuppliers(Array.isArray(supplierResponse?.data) ? supplierResponse.data : Array.isArray(supplierResponse) ? supplierResponse : []);
    setUsers(Array.isArray(usersResponse?.data) ? usersResponse.data : Array.isArray(usersResponse) ? usersResponse : []);
  };

  useEffect(() => {
    if (!show) return;
    loadData().catch((error) => console.error('Failed to load assign to contractor options', error));
  }, [show]);

  useEffect(() => {
    if (!show) return;
    const contractorId = Number(formData.globalContractorId);
    if (!Number.isFinite(contractorId) || contractorId <= 0) {
      setExistingWorkPackages([]);
      setSelectedWorkPackageId('new');
      return;
    }

    getWorkPackages({
      globalContractorId: contractorId,
      tenantId: Number(userProfile?.tenantId),
    })
      .then((response) => {
        const packages = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        setExistingWorkPackages(packages);
        setSelectedWorkPackageId('new');
      })
      .catch((error) => {
        console.error('Failed to load contractor work packages', error);
        setExistingWorkPackages([]);
        setSelectedWorkPackageId('new');
      });
  }, [formData.globalContractorId, show, userProfile?.tenantId]);

  const wizardApi = useContractorWizard({
    contractors,
    suppliers,
    onSaved: async (created) => {
      await loadData();
      const createdId = created?.id || created?.data?.id;
      if (createdId) setFormData((prev) => ({ ...prev, globalContractorId: createdId }));
    },
  });

  useEffect(() => {
    if (!show) return;
    setFormData(buildInitialFormData({ workOrder, form, shiftStartTime }));
    setErrors({});
    setSelectedWorkPackageId('new');
  }, [show, workOrder, form, shiftStartTime]);

  useEffect(() => {
    const startAtDate = formData.startAt ? new Date(formData.startAt) : null;
    if (!startAtDate || Number.isNaN(startAtDate.getTime())) return setFormData((prev) => ({ ...prev, expiryDate: '' }));
    const expiryDate = new Date(startAtDate);
    expiryDate.setDate(expiryDate.getDate() + Number(formData.jobExpiryDays || 0));
    setFormData((prev) => ({ ...prev, expiryDate: expiryDate.toISOString() }));
  }, [formData.jobExpiryDays, formData.startAt]);

  const supplierById = useMemo(() => {
    const map = new Map();
    suppliers.forEach((supplier) => map.set(Number(supplier.id), supplier));
    return map;
  }, [suppliers]);

  const contractorOptions = useMemo(() => {
    const options = contractors.map((contractor) => {
      const supplier = supplierById.get(Number(contractor.supplierId));
      return {
        value: contractor.id,
        label: contractor.name,
        contractorName: contractor.name,
        supplierName: supplier?.name || contractor?.supplier?.name || 'No Supplier',
        avatarUrl: getContractorImageUrl(contractor),
      };
    });
    return [{ value: ADD_CONTRACTOR_VALUE, label: '+ Add Contractor', isAddContractor: true }, ...options];
  }, [contractors, supplierById]);

  const reportToOptions = useMemo(() => {
    const targetDepartmentIds = getAssignmentDepartmentIds({ form, workOrder, isFormShare });
    const canReportToAllDepartments = targetDepartmentIds.includes(-1);

    return users.filter((user) => {
      if (!canReportToAllDepartments && targetDepartmentIds.length === 0) return false;
      let departments = [];
      if (Array.isArray(user?.departments)) departments = user.departments;
      else if (typeof user?.departments === 'string') {
        try { const parsed = JSON.parse(user.departments || '[]'); departments = Array.isArray(parsed) ? parsed : []; } catch (_error) { departments = []; }
      }

      if (canReportToAllDepartments) return Array.isArray(departments) && departments.length > 0;

      return Array.isArray(departments)
        && departments.some((id) => targetDepartmentIds.includes(Number(id)));
    }).map((user) => ({ value: user.id, label: user.name || user.email || `User ${user.id}` }));
  }, [users, form, isFormShare, workOrder]);

  const fields = useMemo(() => ([
    {
      field: 'globalContractorId',
      label: 'Contractor',
      type: 'select',
      options: contractorOptions,
      required: true,
      size: 12,
      onChange: (option, setData, field) => {
        if (option?.value === ADD_CONTRACTOR_VALUE) {
          wizardApi.openWizard();
          setData((prev) => ({ ...prev, [field]: null }));
          return;
        }
        setData((prev) => ({ ...prev, [field]: option?.value ?? null }));
      },
      formatOptionLabel: (option) => option.isAddContractor ? (
        <span className='fw-semibold text-primary'>+ Add Contractor</span>
      ) : (
        <div className='d-flex align-items-center gap-2'>
          <img src={option.avatarUrl} alt={option.contractorName} style={{ width: '25px', height: '25px', borderRadius: '50%', objectFit: 'cover' }} />
          <div>
            <div><span className="fw-semibold pe-16">{option.contractorName}</span><span className="text-secondary text-sm">{option.supplierName}</span></div>
          </div>
        </div>
      ),
    },
    { field: 'reportTo', label: 'Report To', type: 'text', size: 6, list: 'reporting-by-options', placeholder: 'Select or type a name' },
    { field: 'jobExpiryDays', label: 'Job Expiry', type: 'select', options: EXPIRY_OPTIONS, required: true, size: 6 },
    { field: 'startAt', label: 'Start Time', type: 'datetime', required: true, size: 6 },
    { field: 'duration', label: 'Estimated Duration (Hours)', type: 'number', required: true, size: 6, min: 0, step: '0.01' },
    { field: 'meetingPoint', label: 'Meeting Point', type: 'text', maxLength: 255, size: 12 },
    { field: 'location', label: 'Location', type: 'text', maxLength: 255, size: 12 },
    { field: 'instructions', label: 'Notes', type: 'textarea', size: 12 },
  ]), [contractorOptions, reportToOptions, wizardApi]);

  const { renderField } = useFormBuilder(fields, formData, setFormData, errors);
  const selectedContractor = contractorOptions.find((option) => Number(option.value) === Number(formData.globalContractorId));

  const createAssignedWorkOrderForm = async ({ contractorId, contractorName, assignedAtUtc }) => {
    const userId = Number(userProfile?.id) || null;
    let newWorkOrderForm = null;
    const createdActionIds = [];

    try {
      newWorkOrderForm = await createWorkOrderForm({
        formId: Number(form?.id),
        workOrderId: null,
        assetId: null,
        locationId: 0,
        createdBy: userId,
      });

      const formDetails = await getFormById(form.id);
      if (!formDetails) {
        throw new Error('Unable to load form details');
      }

      const items = formDetails?.tblFormItems || [];
      await Promise.all(
        items.map((item) =>
          createFormData({
            formItemId: item.id,
            formId: item.formId,
            workOrderFormId: newWorkOrderForm.id,
            description: item.description,
            width: item.width,
            images: item.images,
            heading: item.heading,
            formInputTypeId: item.formInputTypeId,
            instructions: item.instructions,
            includePhoto: item.includePhoto,
            disableComments: item.disableComments,
            raiseDefects: item.raiseDefects,
            required: item.required,
            data: item.data || null,
            sortOrder: item.sortOrder,
            assetId: null,
            departmentId: null,
            teamId: null,
            createdBy: userId,
          }),
        ),
      );

      const actions = formDetails?.tblFormActions || [];
      await Promise.all(
        actions.map((action) =>
          createWorkOrderFormAction({
            workOrderFormId: newWorkOrderForm.id,
            actionData: action.actionData,
            formActionId: action.formActionId,
            createdBy: userId,
          }).then((createdAction) => {
            if (createdAction?.id) createdActionIds.push(createdAction.id);
            return createdAction;
          }),
        ),
      );

      await updateWorkOrderForm(newWorkOrderForm.id, {
        assigned: 1,
        assignedAt: assignedAtUtc,
        assignedToName: contractorName,
        assignedContractorId: contractorId,
        updatedBy: userId,
      });

      return newWorkOrderForm;
    } catch (error) {
      if (newWorkOrderForm?.id) {
        try {
          const actionIdsToDelete = createdActionIds.length > 0
            ? createdActionIds
            : ((await getWorkOrderFormActionsByWorkOrderFormId(newWorkOrderForm.id)) || [])
              .map((action) => action?.id)
              .filter((id) => Number.isFinite(Number(id)));

          if (actionIdsToDelete.length > 0) {
            await Promise.all(actionIdsToDelete.map((actionId) => deleteWorkOrderFormAction(actionId)));
          }

          await deleteWorkOrderForm(newWorkOrderForm.id);
        } catch (cleanupError) {
          console.error('Error rolling back newly assigned work order form', cleanupError);
        }
      }
      throw error;
    }
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!formData.globalContractorId) nextErrors.globalContractorId = 'Contractor is required';
    if (!formData.startAt) nextErrors.startAt = 'Start time is required';
    if (formData.duration === '' || formData.duration === null || Number.isNaN(Number(formData.duration))) nextErrors.duration = 'Estimated duration must be a decimal number';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSaving(true);
    try {
      const assignedAtUtc = new Date().toISOString();
      let assignedWorkOrderForm = null;

      if (isFormShare && form) {
        assignedWorkOrderForm = await createAssignedWorkOrderForm({
          contractorId: Number(formData.globalContractorId),
          contractorName: selectedContractor?.contractorName || selectedContractor?.label || '',
          assignedAtUtc,
        });
      }

      const payload = {
        workOrderId: isFormShare ? null : Number(workOrder?.id),
        formId: isFormShare
          ? Number(assignedWorkOrderForm?.id || form?.id)
          : null,
        globalContractorId: Number(formData.globalContractorId),
        reportTo: (formData.reportTo || '').trim(),
        startAt: formData.startAt,
        duration: Number(formData.duration),
        expiryDate: formData.expiryDate,
        meetingPoint: formData.meetingPoint,
        location: formData.location,
        instructions: formData.instructions,
        selectedWorkPackageId: selectedWorkPackageId === 'new' ? null : Number(selectedWorkPackageId),
        workOrderDetails: !isFormShare && workOrder ? {
          id: workOrder.id,
          scheduledAt: workOrder.scheduledAt,
          dueDate: workOrder.dueDate,
          meetingPoint: workOrder.meetingPoint,
          locationAddress: workOrder.locationAddress || '',
        } : undefined,
        formDetails: isFormShare && form ? {
          id: assignedWorkOrderForm?.id || form.id,
          name: form.name,
          formTypeName: form.formType?.name || form.category?.name || null,
        } : undefined,
      };
      let response;
      try {
        response = await shareWorkPackage(payload);

        if (!isFormShare && workOrder?.id) {
          const assignedContractorId = Number(formData.globalContractorId);
          if (Number.isFinite(assignedContractorId)) {
            const existingContractors = toContractorIdArray(workOrder?.contractors);
            const nextContractors = existingContractors.includes(assignedContractorId)
              ? existingContractors
              : [...existingContractors, assignedContractorId];

            await update('work-orders', workOrder.id, { contractors: nextContractors });
          }
        }
      } catch (shareError) {
        if (isFormShare && assignedWorkOrderForm?.id) {
          try {
            await deleteWorkOrderForm(assignedWorkOrderForm.id);
          } catch (cleanupError) {
            console.error('Failed to clean up assigned form after share failure', cleanupError);
          }
        }
        throw shareError;
      }

      if (onSaved) onSaved(response);
      onClose();
    } catch (error) {
      console.error(`Failed to share ${isFormShare ? 'form' : 'work order'} with contractor`, error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className='modal fade show' style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} role='dialog' aria-modal='true'>
        <div className='modal-dialog modal-lg modal-dialog-scrollable'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>Assign To Contractor</h5>
              <button type='button' className='btn-close' onClick={onClose} />
            </div>
            <div className='modal-body'>
              <div className='row g-3'>
                {fields.map((field) => (
                  <React.Fragment key={field.field}>
                    {field.field === 'reportTo' ? (
                      <div className='col-md-6'>
                        <label className='form-label'>Report To</label>
                        <input
                          type='text'
                          className={`form-control ${errors.reportTo ? 'is-invalid' : ''}`}
                          value={formData.reportTo || ''}
                          list='reporting-by-options'
                          placeholder='Select or type a name'
                          onChange={(event) => {
                            setFormData((prev) => ({ ...prev, reportTo: event.target.value }));
                          }}
                        />
                        <datalist id='reporting-by-options'>
                          {reportToOptions.map((option) => (
                            <option key={option.value} value={option.label} />
                          ))}
                        </datalist>
                        {errors.reportTo && <div className='invalid-feedback d-block'>{errors.reportTo}</div>}
                      </div>
                    ) : renderField(field)}
                    {field.field === 'globalContractorId' && existingWorkPackages.length > 0 && (
                      <div className='col-12 mt-24'>
                        <div className="alert alert-warning px-24 mb-12">
                          <p className='mb-6 text-lg fw-bold'>
                            Work Packages Exist for this Contractor</p>
                          <p className="mb-8">Choose to create a new Work Package or add to an exising one.</p>

                          <div className='list-group mb-6 border-none'>
                            <label className='list-group-item d-flex align-items-center gap-2 bg-transparent border-0 my-6'>
                              <input
                                className='form-check-input me-2 mt-0'
                                type='radio'
                                name='workPackageSelection'
                                value='new'
                                checked={selectedWorkPackageId === 'new'}
                                onChange={() => setSelectedWorkPackageId('new')}
                              />
                              <span className='fw-semibold' style={{ color: "var(--bs-warning-text-emphasis)" }}>Create a New Work Package</span>
                            </label>

                            {existingWorkPackages.map((pkg) => (
                              <label
                                key={pkg.id}
                                className='list-group-item d-flex align-items-center gap-2 bg-transparent border-0 py-6'
                              >
                                <input
                                  className='form-check-input me-2 mt-0'
                                  type='radio'
                                  name='workPackageSelection'
                                  value={pkg.id}
                                  checked={Number(selectedWorkPackageId) === Number(pkg.id)}
                                  onChange={() => setSelectedWorkPackageId(pkg.id)}
                                />
                                <span>
                                  <span className='fw-semibold' style={{ color: "var(--bs-warning-text-emphasis)" }}>Add to Work Package #{pkg.id}{' '}</span>
                                  <span className='ps-8 text-sm' style={{ color: "var(--bs-warning-text-emphasis)" }}>
                                    Expires{' '}
                                    {pkg.expiryDate
                                      ? new Date(pkg.expiryDate).toLocaleString()
                                      : '—'}
                                  </span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <hr />
              <div className='small text-secondary'>
                {isFormShare ? (
                  <>
                    <div><strong>Form:</strong> {form?.name || '—'}</div>
                    <div><strong>Form Type:</strong> {form?.formType?.name || form?.category?.name || '—'}</div>
                  </>
                ) : (
                  <>
                    <div><strong>Work Order Number:</strong> {workOrder?.workOrderNumber || '—'}</div>
                    <div><strong>Work Order Title:</strong> {workOrder?.workOrderTitle || '—'}</div>
                    <div><strong>Asset / Area:</strong> {workOrder?.asset ? `${workOrder.asset.assetNumber || ''} ${workOrder.asset.assetDescription || ''}`.trim() : workOrder?.area?.name || '—'}</div>
                  </>
                )}
              </div>
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-secondary' onClick={onClose} disabled={isSaving}>Cancel</button>
              <button type='button' className='btn btn-primary' onClick={handleSubmit} disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      </div>
      <ContractorWizardModal wizardApi={wizardApi} />
    </>
  );
};

export default WorkOrderShare;
